      <span class="trigger-label">Triangulation Assistant</span>
    </button>

    <!-- Side Panel Drawer -->
    <transition name="drawer">
      <div v-if="isOpen" class="focus-drawer shadow-2xl">
        <!-- Header -->
        <div class="drawer-header">
          <div class="h-left">
            <b-icon icon="lightning-fill" class="lightning-icon"></b-icon>
            <h5>Triangulation Context</h5>
          </div>
          <button class="close-drawer" @click="toggleFocus">
            <b-icon icon="chevron-right"></b-icon>
          </button>
        </div>

        <!-- Body -->
        <div class="drawer-body p-4">
          <div v-if="loading" class="analysis-loader">
            <div class="pulse-ring"></div>
            <p>Translating data discrepancies...</p>
          </div>

          <div v-else class="findings-container">
            <!-- Summary Card -->
            <div class="finding-card primary mb-4">
              <div class="card-tag">CONTEXTUAL ANALYSIS</div>
              <p class="finding-text">
                Current analysis shows a <strong>{{ consistencyScore }}% alignment</strong> between
                <strong>{{ primarySource }}</strong> and <strong>{{ comparisonSources.join(' & ') }}</strong>.
              </p>
              <div class="recommendation mt-3">
                <strong>Why the Gap?</strong> {{ methodologyExplanation }}
              </div>
            </div>

            <!-- Focus Markers -->
            <div class="focus-section mb-4">
              <h6 class="section-label">Trend Alignment</h6>
              <div class="trend-card">
                <p class="finding-text">
                  Historical data analysis (2023-2026) suggests that <strong>{{ bestAlignedSource }}</strong>
                  currently aligns most closely with stable long-term trends for <strong>{{ indicator }}</strong>.
                </p>
              </div>
            </div>

            <!-- Action Steps -->
            <div class="focus-section">
              <h6 class="section-label">Data Considerations</h6>
              <ul class="action-list">
                <li v-for="(insight, idx) in keyInsights" :key="idx">
                  <div class="step-dot"></div>
                  {{ insight }}
                </li>
              </ul>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="drawer-footer p-3 border-top">
          <b-button variant="teal" class="w-100 py-2 font-weight-bold" @click="refreshFocus">
            <b-icon icon="chat-left-text" class="mr-1"></b-icon> Detailed AI Explanation
          </b-button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'FocusAssistant',
  props: {
    consistencyScore: { type: Number, default: 0 },
    complementarityScore: { type: Number, default: 0 },
    primarySource: { type: String, default: 'N/A' },
    comparisonSources: { type: Array, default: () => [] },
    indicator: { type: String, default: '' },
  },
  data() {
    return {
      isOpen: false,
      loading: false,
      methodologyExplanation: '',
      bestAlignedSource: '',
      keyInsights: [],
    };
  },
  watch: {
    isOpen(val) {
      if (val) this.runAnalysis();
    },
  },
  methods: {
    toggleFocus() { this.isOpen = !this.isOpen; },
    refreshFocus() { this.runAnalysis(); },
    runAnalysis() {
      this.loading = true;
      // In a real scenario, this would call the Ollama/Groq API with the instruction:
      // "Don't suggest values, explain differences in methodology and highlight trend alignment."
      setTimeout(() => {
        this.generateTranslatorInsights();
        this.loading = false;
      }, 1500);
    },
    generateTranslatorInsights() {
      const isFacilityBased = (name) => /NHMIS|HRH|HFR|Facility/i.test(name);
      const isSurveyBased = (name) => /NDHS|MICS|Survey|PMA/i.test(name);

      // 1. Explain 'The Why'
      if (isFacilityBased(this.primarySource) && this.comparisonSources.some((s) => isSurveyBased(s))) {
        this.methodologyExplanation = `The ${this.consistencyScore}% difference exists because ${this.primarySource} is based on routine facility reporting, whereas the comparison sources are periodic surveys. Routine data often captures service delivery volume, while surveys measure population-level prevalence.`;
      } else {
        this.methodologyExplanation = 'The discrepancy likely arises from varying reporting frequencies and verification protocols between these administrative sources.';
      }

      // 2. Identify the Trend
      this.bestAlignedSource = this.consistencyScore > 75 ? this.primarySource : this.comparisonSources[0];

      // 3. Key Observations (Insights)
      this.keyInsights = [
        `Primary source (${this.primarySource}) represents administrative reports.`,
        `Comparison sources represent ${isSurveyBased(this.comparisonSources[0]) ? 'independent surveys' : 'parallel administrative chains'}.`,
        'Discrepancy is stable across the last 3 years, suggesting a methodological offset rather than a data error.',
      ];
    },
  },
};
</script>

<style scoped lang="scss">
.focus-assistant {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 10001; /* Above standard assistant */
}

.focus-trigger {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #007d53;
  padding: 8px 16px;
  border-radius: 40px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  color: white;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  box-shadow: 0 10px 25px -5px rgba(0, 125, 83, 0.4);

  &:hover {
    transform: scale(1.05) translateY(-2px);
    background: #008f5d;
    box-shadow: 0 15px 30px -5px rgba(0, 125, 83, 0.5);
  }

  &.active {
    background: #004d33;
    border-color: #00ffaa;
  }

  .icon-container {
    background: rgba(255, 255, 255, 0.15);
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .trigger-label {
    font-weight: 700;
    font-size: 0.9rem;
    letter-spacing: 0.3px;
  }
}

.focus-drawer {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 400px;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  z-index: 10002;
  border-left: 1px solid #edf2f7;
}

.drawer-header {
  background: #007d53;
  padding: 20px 25px;
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;

  .h-left {
    display: flex;
    align-items: center;
    gap: 12px;

    .lightning-icon {
      color: #ffd700;
      font-size: 1.2rem;
    }

    h5 {
      margin: 0;
      font-weight: 800;
      text-transform: uppercase;
      letter-spacing: 1px;
      font-size: 1.1rem;
    }
  }

  .close-drawer {
    background: rgba(255, 255, 255, 0.2);
    border: none;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.2s;

    &:hover {
      background: rgba(255, 255, 255, 0.3);
    }
  }
}

.drawer-body {
  flex: 1;
  overflow-y: auto;
  background: #f8fafc;
}

.finding-card {
  background: white;
  padding: 20px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  position: relative;
  overflow: hidden;

  &.primary {
    border-left: 5px solid #007d53;
  }

  .card-tag {
    position: absolute;
    top: 0;
    right: 0;
    background: #007d53;
    color: white;
    font-size: 0.6rem;
    font-weight: 900;
    padding: 3px 10px;
    border-bottom-left-radius: 12px;
  }

  .finding-text {
    font-size: 0.95rem;
    line-height: 1.6;
    color: #4a5568;
    margin: 0;
  }

  .recommendation {
    font-size: 0.9rem;
    color: #1a202c;
    background: #f0fff4;
    padding: 12px;
    border-radius: 8px;
    border: 1px solid #c6f6d5;
  }
}

.section-label {
  color: #718096;
  font-weight: 700;
  font-size: 0.75rem;
  text-transform: uppercase;
  margin-bottom: 12px;
  letter-spacing: 1px;
}

.outlier-item {
  display: flex;
  align-items: center;
  background: #fff5f5;
  padding: 10px 15px;
  border-radius: 8px;
  margin-bottom: 8px;
  font-size: 0.85rem;
  color: #c53030;
  border: 1px solid #fed7d7;
}

.action-list {
  list-style: none;
  padding: 0;
  margin: 0;

  li {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 0;
    border-bottom: 1px solid #edf2f7;
    font-size: 0.9rem;
    color: #2d3748;

    .step-dot {
      width: 8px;
      height: 8px;
      background: #007d53;
      border-radius: 50%;
      flex-shrink: 0;
    }
  }
}

.analysis-loader {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;

  p {
    margin-top: 20px;
    color: #718096;
    font-weight: 600;
  }

  .pulse-ring {
    width: 50px;
    height: 50px;
    background: #007d53;
    border-radius: 50%;
    animation: pulse 1.5s infinite ease-in-out;
  }
}

@keyframes pulse {
  0% { transform: scale(0.9); opacity: 0.7; }
  50% { transform: scale(1.1); opacity: 1; }
  100% { transform: scale(0.9); opacity: 0.7; }
}

.drawer-footer {
  background: white;
}

.btn-variant-teal {
  background-color: #007d53;
  color: white;
  &:hover {
    background-color: #006342;
  }
}

.text-success-bold { color: #2f855a; font-weight: 800; }
.text-warning-bold { color: #b7791f; font-weight: 800; }
.text-danger-bold { color: #c53030; font-weight: 800; }

/* Transitions */
.drawer-enter-active, .drawer-leave-active {
  transition: all 0.4s ease-out;
}
.drawer-enter, .drawer-leave-to {
  transform: translateX(100%);
}
</style>
